import { useRef, useState } from "react";
import data from "./timelinedata";
import "./homepage.css";

const years = [
  {
    year: "2026",
    entries: [
      ["October", "Korea_2026"],
      ["July", "ICML_2026"],
      ["March", "Alibaba"],
      ["January", "Cambodia"],
    ],
  },
  {
    year: "2025",
    entries: [
      ["December", "Taiwan", "Egypt", "Turkey"],
      ["November", "EMNLP"],
      ["May", "Xiaomi"],
      ["January", "Japan_2025"],
    ],
  },
  {
    year: "2024",
    entries: [
      ["December", "Australia"],
      ["August", "Begin_NUS"],
      ["July", "Japan_2024", "Graduate_BUAA"],
      ["February", "Vietnam"],
      ["January", "Malaysia"],
    ],
  },
  {
    year: "2023",
    entries: [
      ["December", "NTU"],
      ["November", "Japan"],
      ["October", "Yantai"],
      ["July", "HKU"],
    ],
  },
  { year: "2022", entries: [["January – March", "WinterOlympics"]] },
  {
    year: "Memories",
    entries: [
      [
        "Friends & everyday life",
        "FriendRuihua",
        "FriendsBUAA",
        "FriendQu",
        "FriendsSiaJames",
        "FriendErenrat",
        "FriendsSingapore",
      ],
    ],
  },
];

export default function Timeline() {
  const scroll = useRef(null);
  const sections = useRef({});
  const [active, setActive] = useState("2026");
  const jump = (year) => {
    const panel = scroll.current;
    const section = sections.current[year];
    const top =
      section.getBoundingClientRect().top -
      panel.getBoundingClientRect().top +
      panel.scrollTop;
    panel.scrollTo({
      top,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };
  const trackYear = () => {
    const panel = scroll.current;
    if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2) {
      setActive(years[years.length - 1].year);
      return;
    }
    const top = panel.getBoundingClientRect().top;
    const current = [...years]
      .reverse()
      .find(
        ({ year }) =>
          sections.current[year].getBoundingClientRect().top <= top + 100,
      );
    setActive(current?.year || "2026");
  };
  return (
    <section className="timeline-panel" aria-labelledby="timeline-title">
      <div className="timeline-heading">
        <div>
          <h2 id="timeline-title">Timeline</h2>
        </div>
        <span className="timeline-hint">Work, research & travel</span>
      </div>
      <nav className="year-nav" aria-label="Timeline years">
        {years.map(({ year }) => (
          <button
            key={year}
            aria-current={active === year ? "true" : undefined}
            onClick={() => jump(year)}
          >
            {year}
          </button>
        ))}
      </nav>
      <div
        className="timeline-scroll"
        ref={scroll}
        onScroll={trackYear}
        tabIndex={0}
        role="region"
        aria-label="Life timeline, scroll for earlier years"
      >
        {years.map(({ year, entries }) => (
          <section
            key={year}
            ref={(el) => (sections.current[year] = el)}
            className="timeline-year"
            aria-label={year}
          >
            <h3 className="year-label">
              {year}
              <span>
                {year === "Memories"
                  ? "Undated photos"
                  : year === "2026"
                    ? "The latest chapter"
                    : "Earlier chapters"}
              </span>
            </h3>
            {year === "Memories" ? (
              <MemoriesGallery
                items={entries.flatMap(([, ...keys]) =>
                  keys.map((key) => data[key]),
                )}
              />
            ) : (
              entries.map(([month, ...keys]) => (
                <div className="timeline-month" key={month}>
                  <p className="month-label">{month}</p>
                  {keys.map((key) => (
                    <Entry key={key} info={data[key]} />
                  ))}
                </div>
              ))
            )}
          </section>
        ))}
        <p className="timeline-end">Every chapter leaves a trail.</p>
      </div>
    </section>
  );
}
function Entry({ info }) {
  return (
    <article className={info.image ? "trail-entry with-photo" : "trail-entry"}>
      {info.image && (
        <a
          className="trail-photo"
          href={info.image}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open photo: ${info.title}`}
        >
          <img
            src={info.image}
            alt={info.alt}
            loading="lazy"
            decoding="async"
            width="800"
            height="600"
            style={{ objectPosition: info.imagePosition }}
          />
        </a>
      )}
      <div className="trail-copy">
        <div className="entry-meta">
          <span>
            {info.category ||
              { work: "Work", school: "Education", science: "Research" }[
                info.logo
              ] ||
              "Life & travel"}
          </span>
          {info.time && <time>{info.time}</time>}
        </div>
        {info.title && <h4>{info.title}</h4>}
        <p>{info.context.replace("【Blog】", "")}</p>
      </div>
    </article>
  );
}

function MemoriesGallery({ items }) {
  const rail = useRef(null);
  const [index, setIndex] = useState(0);
  const goTo = (next) => {
    const panel = rail.current;
    panel.scrollTo({
      left: Math.max(0, Math.min(items.length - 1, next)) * panel.clientWidth,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };
  return (
    <div
      className="memories-gallery"
      role="region"
      aria-label="Memories photo gallery"
      aria-roledescription="carousel"
    >
      <div
        className="memories-rail"
        ref={rail}
        tabIndex={0}
        aria-label="Swipe or use arrow keys to browse memories"
        onScroll={() =>
          setIndex(
            Math.round(rail.current.scrollLeft / rail.current.clientWidth),
          )
        }
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          const target = {
            ArrowLeft: index - 1,
            ArrowRight: index + 1,
            Home: 0,
            End: items.length - 1,
          }[event.key];
          if (target !== undefined) {
            event.preventDefault();
            goTo(target);
          }
        }}
      >
        {items.map((info, i) => (
          <figure
            className="memory-slide"
            key={info.image}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${items.length}: ${info.title}`}
          >
            <a
              href={info.image}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open photo: ${info.title}`}
              tabIndex={i === index ? 0 : -1}
            >
              <img
                src={info.image}
                alt={info.alt || info.title}
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
              />
            </a>
            <figcaption>
              <h4>{info.title}</h4>
              <p>{info.context}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="memories-controls">
        <button
          type="button"
          aria-label="Previous memory"
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
        >
          ←
        </button>
        <span aria-live="polite" aria-atomic="true">
          {index + 1} / {items.length}
        </span>
        <button
          type="button"
          aria-label="Next memory"
          disabled={index === items.length - 1}
          onClick={() => goTo(index + 1)}
        >
          →
        </button>
      </div>
    </div>
  );
}
